<script setup lang="ts" generic="TValue extends string | number | boolean">
import { computed } from 'vue'
import { ElRadioGroup } from 'element-plus'
import type { Component } from 'vue'

import { useViewport } from '@/shared/lib'
import { Icon } from '@/shared/ui/Icon'
import { Select } from '@/shared/ui/Select'

import RadioButton from './RadioButton.vue'

/**
 * A strip of buttons over a small, fixed set of choices — the shape a form takes for two to
 * nine options that are worth reading at once. More than that, or anything searchable, is
 * a `Select`.
 *
 * **Pass the buttons as `options`**, and the group is generic over what they carry: a group
 * built from a string enum hands that enum to its handler, so no `$event as …` cast is
 * needed. The type is inferred from `options`, or from `modelValue` for a group whose
 * buttons come from the slot.
 *
 * Bind it to a boolean field through `flagOptions`, and to numeric ids
 * through `idsToOptions`.
 */
const props = defineProps<{
  modelValue?: TValue | null
  /**
   * The buttons, as data — the same option shape `Select` and `CheckboxGroup` take. Do not
   * `v-for` `RadioButton`s to build a list; the slot is for a group that is *not* a list: a
   * static pair, or a button with more than a label inside it.
   *
   * An option carrying an `icon` is drawn as that icon with its label as the button's
   * title, so a set of glyphs stays readable on hover.
   */
  options?: Array<{
    label: string
    value: TValue
    disabled?: boolean
    icon?: Component
  }>
  disabled?: boolean
  size?: 'small' | 'default' | 'large'
  /**
   * Lay the buttons out as a grid of this many columns instead of one joined row — for
   * options that mean something as a map (nine positions on the screen read as a 3x3 grid,
   * not as a row of nine). Each cell is then a button of its own rather than a segment of
   * a strip.
   */
  columns?: number
  /**
   * Render the options as a `Select` below 992px, where a long row of buttons wraps into
   * ragged lines. Opt-in on purpose: two or three short buttons still read better as
   * buttons, and only an `options` list can collapse — never the slot or an icon group,
   * whose buttons are the whole point.
   */
  collapseOnMobile?: boolean
}>()

// ElRadioGroup types its emit as `string | number | boolean | undefined` — it knows
// nothing about the options it was handed. Narrowing it back to `TValue` is this
// wrapper's job and happens once, here, which is the whole point of the generic.
const emit = defineEmits<{
  (e: 'update:modelValue', value: TValue): void
  (e: 'change', value: TValue): void
}>()

const { isMobile } = useViewport()

const asSelect = computed(
  () => !!props.collapseOnMobile && isMobile.value && !!props.options?.length
)

/**
 * The select is driven by the option's position rather than by its value: a group's
 * values are strings, numbers *or* booleans, and `Select` speaks the first two. An
 * index is a value every group has, and what is emitted back is the option's own
 * value, so nothing is cast on the way out.
 */
const selectOptions = computed(() =>
  (props.options ?? []).map((option, index) => ({
    label: option.label,
    value: index,
    disabled: option.disabled
  }))
)

const selectedIndex = computed(() => {
  const index = (props.options ?? []).findIndex(
    (option) => option.value === props.modelValue
  )

  return index === -1 ? null : index
})

function pickIndex(index: string | number | null | (string | number)[]) {
  const option = props.options?.[Number(index)]

  if (!option) return

  emit('update:modelValue', option.value)
  emit('change', option.value)
}
</script>

<template>
  <Select
    v-if="asSelect"
    :model-value="selectedIndex"
    :options="selectOptions"
    :disabled="disabled"
    :size="size"
    block
    v-bind="$attrs"
    @update:model-value="pickIndex"
  />

  <ElRadioGroup
    v-else
    class="shared-radio-group"
    :class="{ 'shared-radio-group--grid': !!columns }"
    :style="columns ? { '--shared-radio-group-columns': columns } : undefined"
    :model-value="modelValue ?? undefined"
    :disabled="disabled"
    :size="size"
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event as TValue)"
    @change="emit('change', $event as TValue)"
  >
    <RadioButton
      v-for="option in options"
      :key="String(option.value)"
      :value="option.value"
      :disabled="option.disabled"
      :title="option.icon ? option.label : undefined"
    >
      <Icon v-if="option.icon"><component :is="option.icon" /></Icon>
      <template v-else>{{ option.label }}</template>
    </RadioButton>

    <slot />
  </ElRadioGroup>
</template>

<style lang="scss" scoped>
.shared-radio-group--grid {
  display: grid;
  grid-template-columns: repeat(var(--shared-radio-group-columns, 3), 1fr);
  gap: 4px;

  /* Element joins the buttons of a row into one strip: the inner border-left is dropped
     and only the two ends are rounded. In a grid that leaves cells with a side missing,
     so each one is given its own border and its own corners back. The checked colours
     are more specific and still win; its 1px separator shadow is what `!important`
     drops, the way Element itself drops it on the first button of a strip. */
  :deep(.el-radio-button__inner) {
    width: 100%;
    border: var(--el-border);
    border-radius: var(--el-border-radius-base);
    box-shadow: none !important;
  }
}
</style>
